import { useCompanyConfiguration } from 'init/index';
import { flat, isEmpty, isNonNullish, isNullish, isNumber, map, pipe, prop } from 'remeda';

import type { Automation } from '../../../api';

import { MODEL_TO_ACTION_TYPES } from './action-fields-constants';
import { useAutomationWizardFormContext } from './automation-wizard-form';
import { FIELD_TYPE_TO_ICON, useComputeFields } from './condition-fields-constants';
import type { AutomationFieldType } from './field-types';
import { getEmptyAction } from './initial-state';
import type { AutomationActionType } from './types';

export function useAutomationActionsController() {
  const form = useAutomationWizardFormContext();
  const automation = form.getValues();

  const fields = useComputeFields(automation.targetModel);
  const flattenedFields = pipe(fields, map(prop('items')), flat());

  const {
    package: { automation: automationsPermissionLevel },
  } = useCompanyConfiguration();

  const getAvailableActionTypes = (): Array<{ type: AutomationActionType; enabled: boolean }> => {
    const actionsForModel = MODEL_TO_ACTION_TYPES[automation.targetModel];
    const allActions = actionsForModel.full_automations;
    const enabledActions = actionsForModel[automationsPermissionLevel];

    return allActions.map(actionType => ({
      type: actionType,
      enabled: enabledActions.includes(actionType),
    }));
  };

  const addAction = () => form.insertListItem('actions', getEmptyAction());

  const removeAction = (actionIndex: number) => form.removeListItem('actions', actionIndex);

  const setActionType = (actionIndex: number, actionType: AutomationActionType) =>
    form.setFieldValue(`actions.${actionIndex}.type`, actionType);

  const isActionValid = (action: Automation['actions'][number]) => {
    switch (action.type) {
      case 'CopyValueToField':
        return isNonNullish(action.destinationField) && isNonNullish(action.value);
      case 'CopyFieldToField':
        return isNonNullish(action.sourceField) && isNonNullish(action.destinationField);
      case 'AssignRecoveryPlan':
        return isNonNullish(action.assignableId);
      case 'AssignAccountManager':
        return isNonNullish(action.assignableId);
      case 'Pause':
        return !isEmpty(action.assignableId);
      case 'Unpause':
        return true;
      default:
        return false;
    }
  };

  const isActionEmpty = (action: Automation['actions'][number]) => isNullish(action.type);

  const isActionValidOrEmpty = (action: Automation['actions'][number]): boolean =>
    isActionValid(action) || isActionEmpty(action);

  const getSourceFieldProps = (actionIndex: number) => {
    const action = automation.actions[actionIndex];
    if (isNullish(action) || action.type !== 'CopyFieldToField') return { data: fields };
    const fieldType = flattenedFields.find(f => f.value === action.sourceField)?.type;

    // Filter out the destination from source field options
    const filteredFields = fields.map(group => ({
      ...group,
      items: group.items.filter(field => field.value !== action.destinationField),
    }));

    const leftSection = isNullish(fieldType) ? null : FIELD_TYPE_TO_ICON[fieldType];

    return {
      data: filteredFields,
      leftSection,
      onClear: () => form.setFieldValue(`actions.${actionIndex}.destinationField`, null),
    };
  };

  const getDestinationFieldProps = (actionIndex: number) => {
    const action = automation.actions[actionIndex];
    if (
      action.type === 'AssignAccountManager' ||
      action.type === 'AssignRecoveryPlan' ||
      action.type === 'Pause' ||
      action.type === 'Unpause' ||
      action.type == null
    )
      return {};

    const fieldType = flattenedFields.find(f => f.value === action.destinationField)?.type;
    const leftSection = isNullish(fieldType) ? null : FIELD_TYPE_TO_ICON[fieldType];

    if (action.type === 'CopyValueToField') {
      const writableFields = fields.map(group => ({
        ...group,
        items: group.items.filter(field => field.canWrite),
      }));

      return { data: writableFields, leftSection };
    }

    const sourceFieldType = flattenedFields.find(f => f.value === action.sourceField)?.type;

    // Filter out the source field from destination options
    const filteredFields = fields.map(group => ({
      ...group,
      items: group.items.filter(field => {
        if (field.value === action.sourceField) return false;
        if (!field.canWrite) return false;
        if (sourceFieldType === 'id' && field.type === 'string') return true;
        if (sourceFieldType === 'string' && field.type === 'array') return true;
        if (sourceFieldType === 'array' && field.type === 'string') return true;
        if (field.type !== sourceFieldType) return false;
        return true;
      }),
    }));

    const baseProps = {
      data: filteredFields,
      leftSection,
    };
    const hasAvailableFields = filteredFields.some(group => group.items.length > 0);

    if (isNullish(action.sourceField) || hasAvailableFields) return baseProps;

    // Style hack to use the input description as a hint below the input
    return {
      ...baseProps,
      disabled: true,
      placeholder: '',
      description: 'There are no available fields of this type',
      styles: {
        root: { display: 'flex', flexDirection: 'column', marginTop: 2 } as const,
        input: { marginTop: -4 },
        description: {
          paddingTop: 4,
          order: 2,
        },
      },
    };
  };

  type Action = Automation['actions'][number];
  function getDestinationFieldType(actionIndex: number): AutomationFieldType | null;
  function getDestinationFieldType(action: Action): AutomationFieldType | null;
  function getDestinationFieldType(actionOrIndex: Action | number): AutomationFieldType | null {
    let action: Action;
    if (isNumber(actionOrIndex)) action = form.getValues().actions[actionOrIndex];
    else action = actionOrIndex;

    if (action.type === 'CopyValueToField' || action.type === 'CopyFieldToField') {
      const field = flattenedFields.find(f => f.value === action.destinationField);
      return field ? field.type : null;
    }

    return null;
  }

  return {
    actionsCount: automation.actions.length,
    getSourceFieldProps,
    getDestinationFieldProps,
    addAction,
    removeAction,
    setActionType,
    isActionValid,
    isActionEmpty,
    isActionValidOrEmpty,
    getAvailableActionTypes,
    getDestinationFieldType,
  };
}
